/**
 * Display formatting only. Every amount is computed by the server; the client never does
 * money arithmetic, it only renders the exact decimal strings it receives.
 */

const moneyFormatters = new Map<string, Intl.NumberFormat>();
const compactFormatters = new Map<string, Intl.NumberFormat>();

function formatter(currency: string): Intl.NumberFormat {
  let f = moneyFormatters.get(currency);
  if (!f) {
    f = new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      roundingMode: "halfExpand",
    } as Intl.NumberFormatOptions);
    moneyFormatters.set(currency, f);
  }
  return f;
}

export function formatMoney(value: string | null | undefined, currency: string,
                            opts: { signed?: boolean } = {}): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const text = formatter(currency).format(Math.abs(n));
  if (n < 0) return `−${text}`;
  if (opts.signed && n > 0) return `+${text}`;
  return text;
}

/** Axis ticks and tiles: 1,284 / 12.9K / 4.2M. */
export function formatCompact(value: string | number, currency?: string): string {
  const key = currency ?? "";
  let f = compactFormatters.get(key);
  if (!f) {
    f = new Intl.NumberFormat("en-KE", {
      notation: "compact",
      maximumFractionDigits: 1,
      ...(currency ? { style: "currency", currency } : {}),
    });
    compactFormatters.set(key, f);
  }
  const n = Number(value);
  return n < 0 ? `−${f.format(Math.abs(n))}` : f.format(n);
}

export function formatPercent(value: string | null | undefined, digits = 0): string {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  return `${n.toFixed(digits)}%`;
}

export function signOf(value: string | null | undefined): -1 | 0 | 1 {
  const n = Number(value ?? 0);
  return n > 0 ? 1 : n < 0 ? -1 : 0;
}

const dateFmt = new Intl.DateTimeFormat("en-KE", { day: "numeric", month: "short", year: "numeric" });
const shortDateFmt = new Intl.DateTimeFormat("en-KE", { day: "numeric", month: "short" });
const monthFmt = new Intl.DateTimeFormat("en-KE", { month: "short", year: "numeric" });
const monthShortFmt = new Intl.DateTimeFormat("en-KE", { month: "short" });

/** Calendar dates (YYYY-MM-DD) are timezone-free; parse them as local calendar days. */
export function parseDay(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

export function formatDate(iso: string | null | undefined, short = false): string {
  if (!iso) return "—";
  return (short ? shortDateFmt : dateFmt).format(parseDay(iso));
}

export function formatMonth(month: string, short = false): string {
  return (short ? monthShortFmt : monthFmt).format(parseDay(`${month}-01`));
}

/**
 * A financial period for display: "September 2026" when it is a plain calendar month (the
 * common case - `isCalendarMonth`, lib/period.ts), otherwise its exact bounds, e.g.
 * "26 Aug - 25 Sep 2026" (one year shown, at the end, since a custom period can cross months).
 * `short`: a compact form for tight spaces (a chart axis tick) - "Sep" or just "26 Aug".
 */
export function formatPeriod(start: string, end: string, isCalendarMonth: boolean, short = false): string {
  if (isCalendarMonth) return formatMonth(start.slice(0, 7), short);
  if (short) return shortDateFmt.format(parseDay(start));
  return `${shortDateFmt.format(parseDay(start))} - ${dateFmt.format(parseDay(end))}`;
}

export function formatDateTime(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("en-KE", {
    dateStyle: "medium",
    timeStyle: "short",
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(iso));
}

/** Today's calendar date in the user's timezone, as YYYY-MM-DD. */
export function todayIn(timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone }).format(new Date());
}

export function monthOf(isoDate: string): string {
  return isoDate.slice(0, 7);
}

export function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(y ?? 1970, (m ?? 1) - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function monthBounds(month: string): { from: string; to: string } {
  const [y, m] = month.split("-").map(Number);
  const last = new Date(y ?? 1970, m ?? 1, 0).getDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, "0")}` };
}

export const TRANSACTION_TYPE_LABEL: Record<string, string> = {
  income: "Income",
  expense: "Expense",
  transfer: "Transfer",
  refund: "Refund",
  adjustment: "Adjustment",
};
